var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * Add real time methods to a dataProvider based on a Supabase client.
 *
 * @see https://supabase.com/docs/guides/realtime
 * @see https://supabase.com/docs/guides/realtime/extensions/postgres-changes
 *
 * @param dataProvider The dataProvider to augment
 * @param supabaseClient The Supabase client
 *
 * @example
 * ```tsx
 * import { addRealTimeMethodsBasedOnSupabase } from '@react-admin/ra-realtime';
 * import { supabaseDataProvider } from 'ra-supabase';
 * import { Admin, Resource } from 'react-admin';
 * import { supabaseClient, instanceUrl, apiKey } from './supabaseClient';
 * import { SaleList } from './sales';
 *
 * const dataProvider = supabaseDataProvider({
 *     instanceUrl, apiKey, supabaseClient
 * });
 *
 * const realTimeDataProvider = addRealTimeMethodsBasedOnSupabase({
 *     dataProvider,
 *     supabaseClient,
 * });
 *
 * export const App = () => (
 *     <Admin dataProvider={realTimeDataProvider}>
 *         <Resource name="sales" list={SaleList} />
 *     </Admin>
 * );
 * ```
 */
export var addRealTimeMethodsBasedOnSupabase = function (_a) {
    var dataProvider = _a.dataProvider, supabaseClient = _a.supabaseClient;
    var subscriptions = [];
    var subscribeToChannel = function (topic) { return __awaiter(void 0, void 0, void 0, function () {
        var channels, channel, table, schema, id;
        return __generator(this, function (_a) {
            channels = supabaseClient.getChannels();
            channel = channels.find(function (c) { return c.topic === "realtime:".concat(topic); });
            if (!channel) {
                table = getTableFromTopic(topic);
                schema = 'public';
                id = getIdFromTopic(topic);
                supabaseClient
                    .channel(topic)
                    .on('postgres_changes', {
                    event: '*',
                    schema: schema,
                    table: table,
                    filter: id != null ? "id=eq.".concat(id) : undefined,
                }, function (payload) {
                    subscriptions.forEach(function (subscription) {
                        if (subscription.topic === topic) {
                            subscription.subscriptionCallback({
                                topic: topic,
                                type: getCRUDEventTypeFromSupabaseType(payload.eventType),
                                payload: __assign({ ids: getIdsFromPayload(payload) }, payload),
                            });
                        }
                    });
                })
                    .subscribe();
                return [2 /*return*/, { data: true }];
            }
            else {
                // The channel already exists
                return [2 /*return*/, { data: true }];
            }
            return [2 /*return*/];
        });
    }); };
    var unsubscribeFromChannel = function (topic) { return __awaiter(void 0, void 0, void 0, function () {
        var topicSubscriptions, channels, channel;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    topicSubscriptions = subscriptions.filter(function (subscription) { return subscription.topic === topic; });
                    if (topicSubscriptions.length > 0) {
                        return [2 /*return*/, { data: true }];
                    }
                    channels = supabaseClient.getChannels();
                    channel = channels.find(function (c) { return c.topic === "realtime:".concat(topic); });
                    if (!channel) return [3 /*break*/, 2];
                    return [4 /*yield*/, supabaseClient.removeChannel(channel)];
                case 1:
                    _a.sent();
                    _a.label = 2;
                case 2: return [2 /*return*/, { data: true }];
            }
        });
    }); };
    return __assign(__assign({}, dataProvider), { subscribe: function (topic, subscriptionCallback) { return __awaiter(void 0, void 0, void 0, function () {
            return __generator(this, function (_a) {
                subscriptions.push({
                    topic: topic,
                    subscriptionCallback: subscriptionCallback,
                });
                return [2 /*return*/, subscribeToChannel(topic)];
            });
        }); }, unsubscribe: function (topic, subscriptionCallback) { return __awaiter(void 0, void 0, void 0, function () {
            return __generator(this, function (_a) {
                subscriptions = subscriptions.filter(function (subscription) {
                    return subscription.topic !== topic ||
                        subscription.subscriptionCallback !== subscriptionCallback;
                });
                return [2 /*return*/, unsubscribeFromChannel(topic)];
            });
        }); }, publish: function () {
            // Postgres Changes events should be published by Supabase
            return Promise.reject();
        } });
};
export var getCRUDEventTypeFromSupabaseType = function (eventType) {
    switch (eventType) {
        case 'INSERT':
            return 'created';
        case 'UPDATE':
            return 'updated';
        case 'DELETE':
            return 'deleted';
        default:
            return eventType;
    }
};
export var getTableFromTopic = function (topic) {
    return topic.split('/')[1];
};
export var getIdFromTopic = function (topic) {
    return topic.split('/')[2];
};
export var getIdsFromPayload = function (payload) {
    // Even in the case of an event affecting multiple rows, supabase
    // will publish one event per row, with only one id at a time.
    var data = payload.new;
    var id = data != null ? data.id : undefined;
    return id != null ? [id] : [];
};
